import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllProjects } from '../redux/slice/project.slice';
import { getAllCategories } from '../redux/slice/category.slice';
import { SearchX, ArrowUpRight } from 'lucide-react';
import Container from '../components/common/Container';

const FilterButton = ({ active, label, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold transition-all duration-300 min-w-[120px] sm:min-w-[140px] md:min-w-[160px] border ${active
            ? 'bg-secondary text-white border-secondary shadow-md'
            : 'bg-transparent text-secondary/70 border-secondary/20 hover:border-secondary hover:text-secondary'
            }`}
    >
        {label}
    </button>
);

const ProjectsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('All');

    // Select data from Redux store
    const { projects: rawProjects, loading: projectsLoading } = useSelector((state) => state.project);
    const { categories, loading: categoriesLoading } = useSelector((state) => state.category);

    useEffect(() => {
        if (rawProjects.length === 0) {
            dispatch(getAllProjects());
        }
        if (categories.length === 0) {
            dispatch(getAllCategories());
        }
    }, [dispatch]);

    // Transform data for UI
    const projects = rawProjects.map(p => {
        // Find category name from category list
        const categoryName = categories.find(c => c._id === p.category)?.name || 'Uncategorized';

        return {
            id: p._id,
            title: p.title,
            status: p.status || "Ongoing", // Use backend status
            category: categoryName,
            image: p.images && p.images.length > 0 ? p.images[0].url : "https://via.placeholder.com/800x600?text=No+Image",
            link: `/project/${p.sku}`,
            description: p.description,
        };
    });

    const isLoading = projectsLoading || categoriesLoading;

    // Filters: 'All' + 'Ongoing' + 'Completed' + Category Names
    const filters = ['All', 'Ongoing', 'Completed', ...categories.map(c => c.name)];

    const filteredProjects = filter === 'All'
        ? projects
        : projects.filter(p => p.category === filter || p.status === filter);

    return (
        <div className="min-h-screen bg-background relative overflow-hidden selection:bg-secondary selection:text-white">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-secondary/5 blur-[120px] rounded-full animate-pulse-slow" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-secondary/10 blur-[100px] rounded-full animate-pulse-slow" style={{ animationDelay: '1.5s' }} />
            </div>

            <Container className="relative z-10">
                {/* Header Section */}
                <section className="pt-24 md:pt-32 pb-12 md:pb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto px-4"
                    >
                        <div className="flex items-center justify-center gap-4 mb-6">
                            <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                            <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase">Our Portfolio</span>
                            <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                        </div>
                        <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight font-bold">
                            Crafting <br />
                            <span className="">Premium Spaces</span>
                        </h1>
                        <p className="text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
                            Explore our legacy of excellence. From luxury residences to premium commercial hubs, every project is a masterpiece of design and engineering.
                        </p>
                    </motion.div>

                    {/* Filters */}
                    <div className="mt-8 md:mt-16 px-2 md:px-4 relative z-20">
                        {/* Filter List */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 w-full"
                        >
                            {filters.map((f) => (
                                <FilterButton
                                    key={f}
                                    label={f}
                                    active={filter === f}
                                    onClick={() => setFilter(f)}
                                />
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* Projects Grid */}
                <section className="pb-32">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-12 h-12 border-2 border-secondary/30 border-t-secondary rounded-full animate-spin mb-4"></div>
                            <span className="text-light font-serif tracking-widest text-sm">LOADING PROJECTS...</span>
                        </div>
                    ) : (
                        <>
                            <motion.div
                                layout
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
                            >
                                <AnimatePresence mode="popLayout">
                                    {filteredProjects?.map((project, index) => (
                                        <motion.div
                                            layout
                                            key={project.id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            transition={{ duration: 0.4, delay: index * 0.1 }}
                                            onClick={() => navigate(project.link)}
                                            className="group relative cursor-pointer"
                                        >
                                            {/* Custom Card Design - Light Mode */}
                                            <div className="relative h-80 w-full overflow-hidden rounded-sm bg-white border border-primary/5 shadow-lg group-hover:shadow-2xl group-hover:border-secondary/30 transition-all duration-500">
                                                {/* Image with zoom effect */}
                                                <div className="absolute inset-0 overflow-hidden">
                                                    <img
                                                        src={project.image}
                                                        alt={project.title}
                                                        className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                                                    />
                                                </div>

                                                {/* Gradient Overlay - Light to Dark on Bottom for Text Contrast */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

                                                {/* Status Badge */}
                                                <div className="absolute top-6 left-6 z-10 pointer-events-none">
                                                    <span className="px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-primary uppercase border border-primary/10 bg-white/90 backdrop-blur-md shadow-sm">
                                                        {project.status}
                                                    </span>
                                                </div>

                                                {/* Content - Bottom Alignment */}
                                                <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                                                    <div className="transform translate-y-8 group-hover:translate-y-4 transition-transform duration-500 ease-out">
                                                        {/* Decorative Line & Category */}
                                                        <div className="flex items-center gap-3 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                                                            <div className="h-[1px] w-6 bg-background" />
                                                            <span className="text-background text-xs font-bold tracking-[0.2em] uppercase drop-shadow-sm">
                                                                {project.category}
                                                            </span>
                                                        </div>

                                                        {/* Project Title */}
                                                        <h3 className="text-3xl font-serif text-white mb-4 leading-tight drop-shadow-md group-hover:text-white transition-colors">
                                                            {project.title}
                                                        </h3>

                                                        {/* Description - Animated Reveal */}
                                                        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-in-out">
                                                            <div className="overflow-hidden">
                                                                <p className="text-gray-200 text-sm leading-relaxed line-clamp-2 md:line-clamp-2 mb-6 font-light opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                                                                    {project.description}
                                                                </p>

                                                                {/* CTA Button
                                                                <div className="flex items-center gap-2 text-white group-hover:text-background transition-colors duration-300 opacity-0 group-hover:opacity-100 delay-300">
                                                                    <span className="text-xs uppercase tracking-widest font-bold bg-secondary px-1 py-0.5">View Project</span>
                                                                    <ArrowUpRight size={16} />
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </motion.div>

                            {filteredProjects?.length === 0 && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                    className="flex flex-col items-center justify-center pt-20 md:py-20 text-center px-4"
                                >
                                    <div className="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center mb-6 border border-primary/10 group">
                                        <SearchX size={40} className="text-light group-hover:text-secondary group-hover:scale-110 transition-all duration-500" />
                                    </div>
                                    <h3 className="text-3xl md:text-4xl font-serif text-primary mb-4">No Projects Found</h3>
                                    <p className="text-light max-w-lg mx-auto leading-relaxed text-lg mb-8">
                                        We couldn't find any projects matching your current filters.
                                        Try adjusting your search criteria.
                                    </p>
                                    <button
                                        onClick={() => setFilter('All')}
                                        className="group relative px-8 py-3 rounded-full overflow-hidden transition-transform active:scale-95"
                                    >
                                        <span className="absolute inset-0 bg-secondary transition-transform duration-300 origin-left group-hover:scale-x-100 opacity-90"></span>
                                        <span className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors"></span>
                                        <span className="relative z-10 text-white font-medium tracking-widest text-xs uppercase flex items-center gap-2 transition-colors">
                                            Clear All Filters
                                        </span>
                                    </button>
                                </motion.div>
                            )}
                        </>
                    )}
                </section>
            </Container>
        </div>
    );
};

export default ProjectsPage;
